import './SpecializationSelector.css';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentUser, getUserSpecializations } from '../../api/users_institutions';
import LoadingIndicator from '../Common/LoadingIndicator';
import { GRADE_GROUPS, useSpecializationGrades } from '../../hooks/useSpecializationGrades';

/**
 * Decides between the "University Module Unlocked" notice and the school grade picker,
 * based on the current user's UserSpecialization records, then keeps grade selections
 * for school-type specializations in sync with the backend.
 *
 * @param {Object} props
 * @param {'school'|'verify'} [props.forceStage] - Skip the uni/school auto-detection.
 *   'school' goes straight to the grade editor (the old "manage grades" entry point).
 *   'verify' goes straight to the confirmation screen using `initialSelectedGroups` --
 *   used by Settings.jsx's inline grade picker's "Save & Continue".
 * @param {Object} [props.initialSelectedGroups] - domain -> Set<grade> to show on the verify
 *   screen instead of whatever's currently saved server-side. Only meaningful with
 *   forceStage="verify".
 * @param {Function} [props.onClose] - Called once the flow is dismissed or completed.
 */
const SpecializationSelector = ({ forceStage, initialSelectedGroups, onClose }) => {
    const { t } = useTranslation();

    const [stage, setStage] = useState(null); // null | 'uni' | 'school' | 'verify'
    const [visible, setVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [saving, setSaving] = useState(false);

    const {
        selectedGroups,
        setSelectedGroups,
        toggleGrade,
        totalSelectedGrades,
        loadCurrentGrades,
        saveSelection,
    } = useSpecializationGrades();

    useEffect(() => {
        initializeSelector();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!stage) {
            return undefined;
        }
        const frame = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(frame);
    }, [stage]);

    const initializeSelector = async () => {
        const user = getCurrentUser();
        if (!user?.id) {
            onClose?.();
            return;
        }

        try {
            const specializations = await getUserSpecializations(user.id);
            const hasUniversity = specializations.some(
                (spec) => String(spec.target_domain || '').trim().toLowerCase() === 'university'
            );

            if (forceStage !== 'school' && forceStage !== 'verify' && hasUniversity) {
                setStage('uni');
                return;
            }

            const schoolSpecializations = specializations.filter(
                (spec) => String(spec.target_domain || '').trim().toLowerCase() !== 'university'
            );

            if (schoolSpecializations.length === 0 && !initialSelectedGroups) {
                onClose?.();
                return;
            }

            // Always loads a fresh backend snapshot as the diff baseline saveSelection() will
            // compare against -- when forceStage="verify", the selection shown is overridden
            // right after with whatever Settings.jsx's inline picker already had the user pick.
            await loadCurrentGrades();

            if (forceStage === 'verify' && initialSelectedGroups) {
                setSelectedGroups(initialSelectedGroups);
                setStage('verify');
                return;
            }

            setStage('school');
        } catch (error) {
            console.error('Error initializing specialization selector:', error);
            onClose?.();
        }
    };

    const dismiss = (delay = 300) => {
        setVisible(false);
        setTimeout(() => {
            setStage(null);
            onClose?.();
        }, delay);
    };

    const goToStage = (nextStage, delay = 200) => {
        setVisible(false);
        setTimeout(() => setStage(nextStage), delay);
    };

    const proceedToVerification = () => {
        if (totalSelectedGrades === 0) {
            setErrorMessage(t('specializationSelector.selectAtLeastOne'));
            return;
        }
        setErrorMessage('');
        goToStage('verify');
    };

    const editGrades = () => {
        goToStage('school');
    };

    const confirmFinal = async () => {
        const user = getCurrentUser();
        if (!user?.id || saving) {
            return;
        }

        setSaving(true);
        try {
            await saveSelection();
        } catch (error) {
            console.error('Error saving grade selections:', error);
        } finally {
            setSaving(false);
        }

        dismiss();
    };

    if (!stage) {
        return null;
    }

    return (
        <>
            <LoadingIndicator active={saving} label={t('common.loading')} />
            {stage === 'uni' && (
                <div id="uni-modal-overlay" className={`modal-overlay${visible ? ' show' : ''}`}>
                    <div className="modal-content" style={{ maxWidth: '450px' }}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                                    <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                                </svg>
                            </div>
                            <h2 className="modal-title">{t('specializationSelector.uniTitle')}</h2>
                            <p className="modal-subtitle">{t('specializationSelector.uniSubtitle')}</p>
                        </div>
                        <button className="btn-primary" onClick={() => dismiss()}>
                            {t('specializationSelector.enterPlatform')}
                        </button>
                    </div>
                </div>
            )}

            {stage === 'school' && (
                <div id="school-modal-overlay" className={`modal-overlay${visible ? ' show' : ''}`}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                </svg>
                            </div>
                            <h2 className="modal-title">{t('specializationSelector.schoolTitle')}</h2>
                            <p className="modal-subtitle">{t('specializationSelector.schoolSubtitle')}</p>
                        </div>

                        <div className="grades-container">
                            {GRADE_GROUPS.map((group) => (
                                <div className="grade-group" key={group.domain}>
                                    <h4>{t(group.labelKey)}</h4>
                                    <div className="grade-pills">
                                        {group.grades.map((grade) => (
                                            <label className="grade-pill" key={grade}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedGroups[group.domain]?.has(grade) || false}
                                                    onChange={() => toggleGrade(group.domain, grade)}
                                                />
                                                <span>{grade}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {errorMessage && (
                            <div className="alert-msg alert-error" style={{ marginBottom: '16px' }}>{errorMessage}</div>
                        )}

                        <button className="btn-primary" onClick={proceedToVerification}>
                            {t('specializationSelector.saveContinue')}
                        </button>
                    </div>
                </div>
            )}

            {stage === 'verify' && (
                <div id="verification-modal-overlay" className={`modal-overlay${visible ? ' show' : ''}`}>
                    <div className="modal-content" style={{ maxWidth: '480px' }}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                            </div>
                            <h2 className="modal-title">{t('specializationSelector.verifyTitle')}</h2>
                            <p className="modal-subtitle">{t('specializationSelector.verifySubtitle')}</p>
                        </div>

                        <div id="grades-preview-container" className="selected-grades-preview">
                            {GRADE_GROUPS.flatMap((group) => [...(selectedGroups[group.domain] || [])]).map((grade) => (
                                <span className="preview-pill" key={grade}>{grade}</span>
                            ))}
                        </div>

                        <p className="modal-info-text">
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '4px' }} viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                            </svg>
                            {t('specializationSelector.infoText')}
                        </p>

                        <div className="btn-group">
                            {/* Editing already happened inline in Settings.jsx's sd-panel when this
                                selection came from there (signaled by initialSelectedGroups) --
                                looping back into this modal's own school stage would just be a
                                redundant second editor, so skip the option entirely in that case. */}
                            {!initialSelectedGroups && (
                                <button className="btn-outline" onClick={editGrades} disabled={saving}>
                                    {t('specializationSelector.edit')}
                                </button>
                            )}
                            <button className="btn-primary" onClick={confirmFinal} disabled={saving}>
                                {t('specializationSelector.confirmContinue')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SpecializationSelector;
